<template>
    <v-card outlined>
        <v-card-text>
            <v-row>
                <!-- 4) Feedbacks per category per week (January example) -->
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="text-body-1 font-weight-medium d-flex align-center justify-space-between">
                            <span>{{ $t('Saapuneet / luokka') }}</span>

                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-btn icon :disabled="perCategoryWeeklyLoading" @click="goPrevMonth">
                                    <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
                                </v-btn>

                                <v-chip small outlined>
                                    {{ perCategoryWeeklyMonthLabel }}
                                </v-chip>

                                <v-btn icon :disabled="perCategoryWeeklyLoading || !perCategoryWeeklyMonth"
                                    @click="goNextMonth">
                                    <v-icon>{{ icons.mdiArrowRight }}</v-icon>
                                </v-btn>

                                <!-- Only show reset when we're NOT already in rolling mode -->
                                <v-btn v-if="perCategoryWeeklyMonth" text small :disabled="perCategoryWeeklyLoading"
                                    @click="resetToRolling">
                                    {{ $t('Viimeiset 8 viikkoa') }}
                                </v-btn>
                            </div>
                        </v-card-title>
                        <v-card-text style="min-height: 400px;">
                            <div v-if="perCategoryWeeklyLoading" class="text-center mt-5 pt-5">
                                <v-progress-circular :size="40" :width="4" indeterminate></v-progress-circular>
                            </div>
                            <vue-apex-charts v-else type="line" height="320" :options="perCategoryWeeklyOptions"
                                :series="perCategoryWeeklySeries" />
                        </v-card-text>
                    </v-card>
                </v-col>

            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import adminApi from "@/api/admin";
import { mdiArrowLeft, mdiArrowRight } from '@mdi/js'

export default {
    name: "FeedbackDashboardReports",
    components: {
        VueApexCharts,
    },
    data() {
        return {
            perCategoryWeeklySeries: [],
            perCategoryWeeklyOptions: {
                chart: {
                    toolbar: { show: true },
                },
                stroke: {
                    curve: "smooth",
                    // Alternating widths so overlapping lines create a visible halo effect
                    width: [3, 2, 3, 2, 3, 2, 3, 2],
                    // Each series gets a distinct dash pattern so they're distinguishable
                    // even when lines overlap completely
                    dashArray: [0, 6, 3, 10, 5, 8, 1, 4],
                },
                markers: {
                    // Larger markers make overlapping points easier to notice
                    size: 6,
                    strokeWidth: 2,
                    // Cycling shapes (circle, square, diamond, triangle) per series
                    // so overlapping markers still look different
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories: [],
                    title: { text: this.$t("viikko") },
                },
                yaxis: {
                    title: { text: this.$t("kpl") },
                    min: 0,
                    forceNiceScale: true,
                },
                legend: {
                    position: "top",
                },
                tooltip: {
                    // shared: show all series values on hover, even fully hidden ones
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: (val) => `${val} ${this.$t("kpl")}`,
                    },
                },
                colors: [],
            },
            perCategoryWeeklyLoading: false,
            perCategoryWeeklyError: null,

            // `null` means "rolling last 8 weeks ending now".
            // When set, it is a string like "2026-01".
            perCategoryWeeklyMonth: null,
            icons: {
                mdiArrowLeft,
                mdiArrowRight
            }
        };
    },

    computed: {
        perCategoryWeeklyMonthLabel() {
            if (!this.perCategoryWeeklyMonth) return this.$t('Viimeiset 8 viikkoa');
            return this.perCategoryWeeklyMonth;
        },
    },

    created() {
        this.fetchPerCategoryWeeklyReport();
    },

    methods: {
        _pad2(n) {
            return String(n).padStart(2, '0');
        },

        _dateToMonthStr(d) {
            return `${d.getFullYear()}-${this._pad2(d.getMonth() + 1)}`;
        },

        _addMonths(monthStr, delta) {
            // monthStr: YYYY-MM
            const [y, m] = monthStr.split('-').map((x) => parseInt(x, 10));
            const d = new Date(y, (m - 1) + delta, 1);
            return this._dateToMonthStr(d);
        },

        _hslColor(h, s = 70, l = 50) {
            return `hsl(${h}, ${s}%, ${l}%)`;
        },

        _buildSeriesColors(count) {
            // Generate N visually distinct colors.
            // Using golden angle gives a decent spread without needing a hardcoded palette.
            const colors = [];
            const goldenAngle = 137.508;
            for (let i = 0; i < count; i++) {
                colors.push(this._hslColor((i * goldenAngle) % 360));
            }
            return colors;
        },

        async fetchPerCategoryWeeklyReport({ month = null } = {}) {
            this.perCategoryWeeklyLoading = true;
            this.perCategoryWeeklyError = null;

            try {
                const response = await adminApi.getFeedbackPerCategoryWeeklyReport({ month });
                const payload = response?.data || {};
                const weeks = Array.isArray(payload.weeks) ? payload.weeks : [];
                const dataByWeek = payload.data && typeof payload.data === 'object' ? payload.data : {};

                if (!weeks.length) {
                    throw new Error('Missing weeks');
                }

                // Collect categories across all buckets (ensures stable series order)
                const categoryIndex = new Map();
                const categories = [];

                for (const weekLabel of weeks) {
                    const rows = Array.isArray(dataByWeek[weekLabel]) ? dataByWeek[weekLabel] : [];
                    for (const row of rows) {
                        const id = row?.category_id ?? null;
                        const key = id === null ? 'null' : String(id);
                        if (!categoryIndex.has(key)) {
                            categoryIndex.set(key, categories.length);
                            categories.push({
                                key,
                                category_id: id,
                                category: row?.category || (id === null ? this.$t('Ei luokittelua') : this.$t('Luokittelu')),
                            });
                        }
                    }
                }

                // Build series data arrays
                const series = categories.map((c) => ({ name: c.category, data: Array(weeks.length).fill(0) }));
                for (let i = 0; i < weeks.length; i++) {
                    const weekLabel = weeks[i];
                    const rows = Array.isArray(dataByWeek[weekLabel]) ? dataByWeek[weekLabel] : [];
                    for (const row of rows) {
                        const id = row?.category_id ?? null;
                        const key = id === null ? 'null' : String(id);
                        const sIdx = categoryIndex.get(key);
                        if (sIdx !== undefined) {
                            const count = Number(row?.count ?? 0);
                            series[sIdx].data[i] = Number.isFinite(count) ? count : 0;
                        }
                    }
                }

                // Update chart
                this.perCategoryWeeklySeries = series;
                this.perCategoryWeeklyOptions = {
                    ...this.perCategoryWeeklyOptions,
                    xaxis: {
                        ...this.perCategoryWeeklyOptions.xaxis,
                        categories: weeks,
                    },
                    colors: this._buildSeriesColors(series.length),
                };

                this.$emit('categories-loaded', categories.filter((c) => c.category_id !== null));
            } catch (e) {
                // Keep the hardcoded example as fallback.
                this.perCategoryWeeklyError = e?.message || 'Failed to load report';
            } finally {
                this.perCategoryWeeklyLoading = false;
            }
        },

        async goPrevMonth() {
            // If we are in rolling mode, start from current month.
            const base = this.perCategoryWeeklyMonth || this._dateToMonthStr(new Date());
            const next = this._addMonths(base, -1);
            this.perCategoryWeeklyMonth = next;
            await this.fetchPerCategoryWeeklyReport({ month: next });
        },

        async goNextMonth() {
            if (!this.perCategoryWeeklyMonth) return;
            const next = this._addMonths(this.perCategoryWeeklyMonth, +1);

            // If user navigates to the current month, we keep `month` mode (end-of-month anchor)
            // rather than switching to rolling automatically.
            this.perCategoryWeeklyMonth = next;
            await this.fetchPerCategoryWeeklyReport({ month: next });
        },

        async resetToRolling() {
            if (!this.perCategoryWeeklyMonth) return;
            this.perCategoryWeeklyMonth = null;
            await this.fetchPerCategoryWeeklyReport({ month: null });
        },
    },
};
</script>

<style scoped></style>
